import React from 'react';

const TechnologyPage: React.FC = () => {
  // Add SEO meta tags
  React.useEffect(() => {
    document.title = "ScanOnce Technology - How Anti-Counterfeit Verification Works | Kenya";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn how ScanOnce\'s revolutionary one-scan cryptographic technology makes counterfeit alcohol impossible. Advanced authentication using SHA-256 hashing and one-time verification.');
    }
  }, []);

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#f8fafc',
      padding: '40px 20px'
    }}>
      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto'
      }}>
        {/* Header Section */}
        <div style={{
          backgroundColor: 'white',
          padding: '60px 40px',
          borderRadius: '20px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: '40px',
          textAlign: 'center'
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '20px',
            lineHeight: '1.2'
          }}>
            ScanOnce Technology - How Anti-Counterfeit Verification Works
          </h1>
          <p style={{
            color: '#64748b',
            fontSize: '1.2rem',
            lineHeight: '1.6',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            Revolutionary cryptographic technology that makes counterfeiting mathematically impossible
          </p>
        </div>

        {/* Revolutionary One-Scan Authentication */}
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{
            color: '#1e293b',
            fontSize: '1.8rem',
            fontWeight: '600',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            🔒 Revolutionary One-Scan Authentication
          </h2>
          <p style={{
            color: '#64748b',
            fontSize: '1.1rem',
            lineHeight: '1.7',
            marginBottom: '20px'
          }}>
            ScanOnce uses advanced cryptographic technology to create authentication 
            tokens that are mathematically impossible to counterfeit or duplicate.
          </p>
          
          {/* Demo Link */}
          <div style={{
            backgroundColor: '#f8fafc',
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0'
          }}>
            <p style={{
              color: '#475569',
              fontSize: '14px',
              margin: '0 0 12px 0',
              fontWeight: '600'
            }}>
              See it in action:
            </p>
            <a 
              href="/verify" 
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '10px 20px',
                textDecoration: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '14px',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Try Interactive Demo
            </a>
          </div>
        </div>

        {/* The Science Behind ScanOnce */}
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{
            color: '#1e293b',
            fontSize: '1.8rem',
            fontWeight: '600',
            marginBottom: '30px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            🧬 The Science Behind ScanOnce
          </h2>

          {/* Cryptographic Token Generation */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{
              color: '#374151',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🔐 Cryptographic Token Generation
            </h3>
            <p style={{
              color: '#64748b',
              fontSize: '1rem',
              lineHeight: '1.7',
              marginBottom: '16px'
            }}>
              Each product receives a unique cryptographic token during manufacturing. 
              These tokens are generated using SHA-256 hashing and split into public 
              and private components for maximum security.
            </p>
            
            {/* Technical Details */}
            <div style={{
              backgroundColor: '#f1f5f9',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1'
            }}>
              <h4 style={{
                color: '#1e293b',
                fontSize: '14px',
                fontWeight: '600',
                marginBottom: '8px'
              }}>
                Technical Implementation:
              </h4>
              <ul style={{
                color: '#475569',
                fontSize: '14px',
                margin: 0,
                paddingLeft: '20px'
              }}>
                <li>SHA-256 cryptographic hashing</li>
                <li>Public/private key architecture</li>
                <li>Unique token per product unit</li>
                <li>Integration with production systems</li>
              </ul>
            </div>
          </div>

          {/* One-Time Verification */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{
              color: '#374151',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ✅ One-Time Verification
            </h3>
            <p style={{
              color: '#64748b',
              fontSize: '1rem',
              lineHeight: '1.7',
              marginBottom: '16px'
            }}>
              When a consumer scans the QR code, our system verifies the token against 
              our secure database and marks it as "used." This process is irreversible 
              and cannot be undone.
            </p>
            
            {/* Process Flow */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                backgroundColor: '#ecfdf5',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #bbf7d0',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>📱</div>
                <h5 style={{ color: '#166534', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>
                  Consumer Scans
                </h5>
                <p style={{ color: '#15803d', fontSize: '12px', margin: 0 }}>
                  QR code scanned before opening
                </p>
              </div>
              
              <div style={{
                backgroundColor: '#eff6ff',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #bfdbfe',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔍</div>
                <h5 style={{ color: '#1d4ed8', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>
                  System Verifies
                </h5>
                <p style={{ color: '#2563eb', fontSize: '12px', margin: 0 }}>
                  Token checked against database
                </p>
              </div>
              
              <div style={{
                backgroundColor: '#fef3c7',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #fbbf24',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔒</div>
                <h5 style={{ color: '#92400e', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>
                  Token Burned
                </h5>
                <p style={{ color: '#d97706', fontSize: '12px', margin: 0 }}>
                  Marked as used permanently
                </p>
              </div>
            </div>
          </div>

          {/* Instant Counterfeit Detection */}
          <div>
            <h3 style={{
              color: '#374151',
              fontSize: '1.3rem',
              fontWeight: '600',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🚨 Instant Counterfeit Detection
            </h3>
            <p style={{
              color: '#64748b',
              fontSize: '1rem',
              lineHeight: '1.7'
            }}>
              Any subsequent scan of the same token immediately triggers our counterfeit 
              detection system, alerting the consumer that the product is fake and 
              potentially dangerous.
            </p>
          </div>
        </div>

        {/* Comparison Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '30px',
          marginBottom: '30px'
        }}>
          {/* Why Traditional Methods Fail */}
          <div style={{
            backgroundColor: '#fef2f2',
            padding: '30px',
            borderRadius: '16px',
            border: '2px solid #fecaca'
          }}>
            <h2 style={{
              color: '#991b1b',
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ❌ Why Traditional Methods Fail
            </h2>
            <ul style={{
              color: '#7f1d1d',
              fontSize: '1rem',
              lineHeight: '1.6',
              margin: 0,
              paddingLeft: '20px'
            }}>
              <li style={{ marginBottom: '8px' }}>Holographic stickers can be replicated</li>
              <li style={{ marginBottom: '8px' }}>Serial numbers can be duplicated</li>
              <li style={{ marginBottom: '8px' }}>QR codes can be copied</li>
              <li style={{ marginBottom: '8px' }}>Physical security features can be counterfeited</li>
            </ul>
          </div>

          {/* ScanOnce Advantages */}
          <div style={{
            backgroundColor: '#f0fdf4',
            padding: '30px',
            borderRadius: '16px',
            border: '2px solid #bbf7d0'
          }}>
            <h2 style={{
              color: '#166534',
              fontSize: '1.5rem',
              fontWeight: '600',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ✅ ScanOnce Advantages
            </h2>
            <ul style={{
              color: '#14532d',
              fontSize: '1rem',
              lineHeight: '1.6',
              margin: 0,
              paddingLeft: '20px'
            }}>
              <li style={{ marginBottom: '8px' }}>Mathematically impossible to duplicate</li>
              <li style={{ marginBottom: '8px' }}>Real-time verification</li>
              <li style={{ marginBottom: '8px' }}>Instant counterfeit alerts</li>
              <li style={{ marginBottom: '8px' }}>Integration with existing production</li>
              <li style={{ marginBottom: '8px' }}>Consumer safety focus</li>
            </ul>
          </div>
        </div>

        {/* Implementation Section */}
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          marginBottom: '30px'
        }}>
          <h2 style={{
            color: '#1e293b',
            fontSize: '1.8rem',
            fontWeight: '600',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            🏭 Implementation for Manufacturers
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '20px'
          }}>
            <div style={{
              padding: '20px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ color: '#374151', marginBottom: '12px', fontWeight: '600' }}>
                1. Production Integration
              </h4>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                Seamless integration with existing bottling and packaging lines. 
                Minimal disruption to current processes.
              </p>
            </div>
            
            <div style={{
              padding: '20px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ color: '#374151', marginBottom: '12px', fontWeight: '600' }}>
                2. Token Generation
              </h4>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                Automated token creation for each product unit. 
                Secure key management and distribution.
              </p>
            </div>
            
            <div style={{
              padding: '20px',
              backgroundColor: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <h4 style={{ color: '#374151', marginBottom: '12px', fontWeight: '600' }}>
                3. Consumer Education
              </h4>
              <p style={{ color: '#64748b', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                Clear instructions for consumers: "Scan before opening." 
                Marketing support and consumer awareness campaigns.
              </p>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div style={{
          backgroundColor: '#f8fafc',
          padding: '40px',
          borderRadius: '20px',
          border: '2px solid #e2e8f0',
          textAlign: 'center'
        }}>
          <h3 style={{
            color: '#1e293b',
            fontSize: '1.5rem',
            fontWeight: '600',
            marginBottom: '16px'
          }}>
            Ready to Implement ScanOnce Technology?
          </h3>
          <p style={{
            color: '#64748b',
            fontSize: '1rem',
            marginBottom: '24px',
            maxWidth: '600px',
            margin: '0 auto 24px'
          }}>
            Partner with us to integrate unbreakable anti-counterfeit technology into your production process.
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <a 
              href="/verify" 
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                padding: '12px 24px',
                textDecoration: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                transition: 'transform 0.3s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              Try Technology Demo
            </a>
            <a 
              href="mailto:karuoyamercy@gmail.com" 
              style={{
                backgroundColor: 'white',
                color: '#475569',
                padding: '12px 24px',
                textDecoration: 'none',
                border: '2px solid #e2e8f0',
                borderRadius: '8px',
                fontWeight: '600',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Schedule Technical Discussion
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnologyPage;
